/**
 * Coderom Polling Utility
 *
 * Merkezi polling yönetimi — tab visibility aware.
 * SSE genişletildiğinde bu utility güncellenecek,
 * component'ler değişmeyecek.
 *
 * Kullanım:
 *   const cleanup = createSmartInterval(() => fetchData(), 30000);
 *   // cleanup() ile interval temizlenir
 */

// ============================================
// POLLING INTERVAL SABİTLERİ (tek merkezi yer)
// ============================================
export const POLLING_INTERVALS = {
  // Chat & Messaging
  CHAT_MESSAGES: 10_000,        // 10s (chat view açıkken)
  CHAT_ROOMS_ACTIVE: 30_000,    // 30s (chat widget açıkken)
  CHAT_ROOMS_BADGE: 30_000,     // 30s (badge güncelleme, minimize)

  // Tickets & Notifications
  TICKETS_UNREAD: 30_000,       // 30s (badge güncelleme)
  NOTIFICATIONS_UNREAD: 60_000, // 60s (badge güncelleme)

  // Dashboard & Sync
  SYNC_STATUS_IDLE: 60_000,     // 60s (sync çalışmıyorken)
  SYNC_STATUS_ACTIVE: 5_000,    // 5s (sync aktifken)
  DASHBOARD_STATS: 300_000,     // 5 dakika
  ACTIVITY_SUMMARY: 60_000,     // 60s

  // Connection & Limits
  EBAY_CONNECTION: 60_000,      // 60s
  PRODUCT_LIMITS: 60_000,       // 60s

  // Orders
  ORDERS_SSE_FALLBACK: 300_000,     // 5 dakika (SSE yokken)
  ORDERS_SSE_CONNECTED: 1_800_000,  // 30 dakika (SSE varken)
} as const;

// ============================================
// TAB VISIBILITY AWARE POLLING
// ============================================

/**
 * Tab görünür mü kontrol et.
 * Gizli tab'da polling yapılmaz → backend yükü azalır.
 */
export const isTabVisible = (): boolean => {
  return typeof document !== 'undefined' ? !document.hidden : true;
};

/**
 * Tab visibility aware interval oluştur.
 *
 * - Tab görünürken: normal polling
 * - Tab gizlendiğinde: polling DURUR
 * - Tab tekrar görünür olunca: HEMEN bir kez çalışır, sonra normal interval devam eder
 *
 * @returns cleanup fonksiyonu (useEffect return'de çağır)
 */
export const createSmartInterval = (
  callback: () => void,
  intervalMs: number,
  options?: {
    /** Tab visible olunca hemen çalıştır (default: true) */
    runOnVisible?: boolean;
    /** İlk çağrıda hemen çalıştır (default: false) */
    runImmediately?: boolean;
  }
): (() => void) => {
  const { runOnVisible = true, runImmediately = false } = options || {};

  let intervalId: ReturnType<typeof setInterval> | null = null;

  const startInterval = () => {
    if (intervalId) return; // Zaten çalışıyor
    intervalId = setInterval(() => {
      if (isTabVisible()) {
        callback();
      }
    }, intervalMs);
  };

  const stopInterval = () => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };

  const handleVisibilityChange = () => {
    if (isTabVisible()) {
      if (runOnVisible) {
        callback(); // Tab visible olunca hemen çalıştır
      }
      startInterval();
    } else {
      stopInterval();
    }
  };

  // Başlat
  if (runImmediately) {
    callback();
  }

  if (isTabVisible()) {
    startInterval();
  }

  document.addEventListener('visibilitychange', handleVisibilityChange);

  // Cleanup fonksiyonu
  return () => {
    stopInterval();
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
};
